// ============================================================
// Badge - unified body-map chip for traits and talents
// (v6.34.0): a 2-letter text abbreviation in a colored chip
// (purple = trait, gold = talent), with the full name as
// tooltip/aria-label. Conditions keep their SVG symbols with
// severity colors (ConditionIcon) - deliberately not unified,
// they are the interpretable ones. Colors live in theme.css.
// ============================================================
import { memo, type CSSProperties, type KeyboardEvent } from 'react';

type BadgeVariant = 'trait' | 'talent';

/** Build a 2-letter abbreviation from a name (e.g. "Whirlwind of Death" → "WD"). */
export function abbreviate(name: string): string {
  const words = name.split(/\s+/).filter((w) => !['of', 'the', 'a', 'an', 'to'].includes(w.toLowerCase()));
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

interface BadgeProps {
  variant: BadgeVariant;
  /** Full name - shown in the tooltip; abbreviated on the chip. */
  label: string;
  /** Numeric parameter (e.g. Daemonic 4, Fear 2) shown after the abbreviation. */
  value?: number;
  /** Dimmed when false (e.g. melee-only talent with a ranged weapon). */
  active?: boolean;
  size?: number;
  onClick?: () => void;
}

/** A badge with a click is a button (Enter and Space press it); without one it is a picture. */
function interaction(onClick: (() => void) | undefined) {
  if (!onClick) return { role: 'img' };
  return {
    role: 'button',
    tabIndex: 0,
    onKeyDown: (e: KeyboardEvent) => (e.key === 'Enter' || e.key === ' ') && onClick(),
  };
}

function badgeStyle(variant: BadgeVariant, active: boolean, size: number, clickable: boolean): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size,
    height: size,
    borderRadius: 3,
    boxSizing: 'border-box',
    background: `var(--badge-${variant}-bg)`,
    border: `1px solid ${active ? `var(--badge-${variant}-border)` : 'var(--badge-inactive-border)'}`,
    color: `var(--badge-${variant}-text)`,
    fontSize: size * 0.38,
    fontWeight: 'bold',
    fontFamily: 'monospace',
    opacity: active ? 1 : 0.35,
    cursor: clickable ? 'pointer' : 'default',
    userSelect: 'none',
  };
}

function Badge({ variant, label, value, active = true, size = 28, onClick }: BadgeProps) {
  const title = value != null ? `${label} (${value})` : label;
  const { role, tabIndex, onKeyDown } = interaction(onClick);
  return (
    <span
      role={role}
      aria-label={title}
      title={title}
      onClick={onClick}
      tabIndex={tabIndex}
      onKeyDown={onKeyDown}
      style={badgeStyle(variant, active, size, !!onClick)}
    >
      {abbreviate(label)}
      {value != null && <span style={{ fontSize: size * 0.28, marginLeft: 1 }}>{value}</span>}
    </span>
  );
}

export default memo(Badge);
